// Chemin: C:\gestion-copro\src\pages\admin\LotsPage.tsx
// Session 47 : Sélecteur copropriétaire avec proprietaireId (source unique)
// Session 58 : Correction bug coproprieteId (était coproprietaireId → maintenant coproprieteIds[0])
// Session 75 : Sécurité suppressions (corbeille Gestionnaire, "Tout supprimer" Admin SaaS uniquement)

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Home, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  Building2,
  Car,
  Archive,
  FileUp,
  Loader2,
  FileSpreadsheet,
  Copy,
  Check,
  Link,
  Unlink,
  ChevronDown,
  ChevronUp,
  Bike,
  Eye,
  Users
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getLotsByCopropriete, 
  createLot, 
  updateLot, 
  deleteLot,
  calculerTotalTantiemes
} from '../../services/lot.service';
import { 
  Lot, 
  LotCreateData, 
  TypeLot, 
  TYPE_LOT_LABELS,
  TYPES_LOT_PRINCIPAL,
  TYPES_LOT_SECONDAIRE
} from '../../types/lot.types';
import { extractLotsFromDocument } from '../../services/extractionService';
import { ExtractedLot, LotsExtractionResult } from '../../types/extraction.types';
import ExtractionValidationModal from '../../components/extraction/ExtractionValidationModal';
// Session 47 : Import des copropriétaires
import { getCoproprietaires } from '../../services/coproprietaire.service';
import { Coproprietaire } from '../../types/coproprietaire.types';

// ============================================
// FONCTIONS UTILITAIRES
// ============================================

// Icône selon le type de lot
const getLotIcon = (type: TypeLot) => {
  switch (type) {
    case 'appartement':
    case 'studio':
      return <Home className="w-4 h-4" />;
    case 'parking':
    case 'parking_couvert':
    case 'parking_exterieur':
      return <Car className="w-4 h-4" />;
    case 'cave':
      return <Archive className="w-4 h-4" />;
    case 'local_velo':
      return <Bike className="w-4 h-4" />;
    case 'local_commercial':
    case 'bureau':
      return <Building2 className="w-4 h-4" />;
    default:
      return <Home className="w-4 h-4" />;
  }
};

// Mapper le type extrait vers TypeLot (extraction IA)
const mapExtractedTypeToTypeLot = (extractedType: string): TypeLot => {
  const type = extractedType.toLowerCase();
  if (type.includes('t1') || type.includes('studio') || type.includes('f1')) return 'studio';
  if (type.includes('t2') || type.includes('f2')) return 'appartement';
  if (type.includes('t3') || type.includes('f3')) return 'appartement';
  if (type.includes('t4') || type.includes('f4')) return 'appartement';
  if (type.includes('t5') || type.includes('f5')) return 'appartement';
  if (type.includes('parking') && type.includes('couvert')) return 'parking_couvert';
  if (type.includes('parking') && type.includes('ext')) return 'parking_exterieur';
  if (type.includes('parking') || type.includes('box') || type.includes('garage')) return 'parking';
  if (type.includes('vélo') || type.includes('velo')) return 'local_velo';
  if (type.includes('cave') || type.includes('cellier')) return 'cave';
  if (type.includes('local') || type.includes('commercial') || type.includes('boutique')) return 'local_commercial';
  if (type.includes('bureau')) return 'bureau';
  return 'appartement';
};

// Mapper la nature CSV vers TypeLot (MAPPING INTELLIGENT)
const mapNatureToTypeLot = (nature: string): TypeLot => {
  const n = nature.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Supprime les accents
  
  // Appartements
  if (n.includes('appartement')) return 'appartement';
  if (n.includes('studio')) return 'studio';
  
  // Parkings (ordre important : vérifier les plus spécifiques d'abord)
  if (n.includes('parking') && n.includes('couvert')) return 'parking_couvert';
  if (n.includes('parking') && (n.includes('ext') || n.includes('exterieur'))) return 'parking_exterieur';
  if (n.includes('parking') || n.includes('garage') || n.includes('box')) return 'parking';
  
  // Local vélo (avant cave pour éviter confusion)
  if (n.includes('velo') || n.includes('vélo') || n.includes('local velo') || n.includes('local vélo')) return 'local_velo';
  
  // Cave / Cellier
  if (n.includes('cave') || n.includes('cellier')) return 'cave';
  
  // Commerces et bureaux
  if (n.includes('local') && n.includes('commercial')) return 'local_commercial';
  if (n.includes('commerce') || n.includes('boutique')) return 'local_commercial';
  if (n.includes('bureau')) return 'bureau';
  
  return 'autre';
};

// Tri numérique des lots
const sortLotsNumerically = (lots: Lot[]): Lot[] => {
  return [...lots].sort((a, b) => {
    const numA = parseInt(a.numero.replace(/\D/g, '')) || 0;
    const numB = parseInt(b.numero.replace(/\D/g, '')) || 0;
    return numA - numB;
  });
};

// Vérifie si un lot est un lot principal
const isLotPrincipal = (lot: Lot): boolean => {
  return TYPES_LOT_PRINCIPAL.includes(lot.type);
};

// Vérifie si un lot est un lot secondaire
const isLotSecondaire = (lot: Lot): boolean => {
  return TYPES_LOT_SECONDAIRE.includes(lot.type);
};

// Formulaire vide par défaut - Session 47 : proprietaireId au lieu de champs texte
const emptyFormData = {
  numero: '',
  type: 'appartement' as TypeLot,
  batiment: '',
  etage: '',
  description: '',
  tantièmesGeneraux: 0,
  proprietaireId: '' // Session 47 : ID du copropriétaire sélectionné (vide = non renseigné)
};

// Prompt à copier pour générer le CSV
const CSV_GENERATION_PROMPT = `Extrais tous les lots de ce règlement de copropriété.
Génère un fichier CSV avec séparateur point-virgule et ces colonnes exactes :
Numéro de lot;Nature;Type;Étage;Tantièmes

Règles :
- Nature = Appartement, Cave, Parking extérieur, Parking couvert, Local vélo, etc.
- Type = T1, T2, T3, T4, T5, Studio (uniquement pour les appartements, vide sinon)
- Étage = RDC, 1er, 2ème, 3ème, etc. (vide si non applicable)
- Tantièmes = valeur numérique uniquement

Trie par numéro de lot croissant.
Ne mets pas d'en-tête de code, génère directement le contenu CSV.`;

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const LotsPage: React.FC = () => {
  const { userProfile } = useAuth();
  // ✅ CORRECTION Session 58 : coproprieteIds est un tableau, pas coproprietaireId
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // Session 75 : Helpers de permission pour les suppressions
  const isAdminSaas = userProfile?.isGestionnaireSaas === true;
  const isGestionnaire = userProfile?.role === 'gestionnaire' || userProfile?.role === 'admin_saas';
  
  // Peut supprimer un lot : Gestionnaire ou Admin SaaS uniquement
  const canDeleteLot = isAdminSaas || isGestionnaire;
  
  // Peut tout supprimer : Admin SaaS uniquement (action dangereuse)
  const canDeleteAll = isAdminSaas;

  // États
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalTantiemes, setTotalTantiemes] = useState(0);
  
  // Session 47 : Liste des copropriétaires pour le sélecteur
  const [coproprietaires, setCoproprietaires] = useState<Coproprietaire[]>([]);
  
  // Modal création/édition
  const [showModal, setShowModal] = useState(false);
  const [editingLot, setEditingLot] = useState<Lot | null>(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  
  // Modal détail lot principal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  
  // Modal rattachement
  const [showRattachementModal, setShowRattachementModal] = useState(false);
  const [rattachementLoading, setRattachementLoading] = useState(false);
  
  // Suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lotToDelete, setLotToDelete] = useState<Lot | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Extraction IA
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<LotsExtractionResult | null>(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [importingLots, setImportingLots] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import CSV
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [importingCSV, setImportingCSV] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // Suppression en masse
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);

  // Section lots non rattachés
  const [showLotsNonRattaches, setShowLotsNonRattaches] = useState(false);

  // ============================================
  // DONNÉES CALCULÉES
  // ============================================

  // Lots principaux (appartements/studios)
  const lotsPrincipaux = useMemo(() => {
    return sortLotsNumerically(lots.filter(isLotPrincipal));
  }, [lots]);

  // Lots secondaires (caves, parkings, etc.)
  const lotsSecondaires = useMemo(() => {
    return sortLotsNumerically(lots.filter(isLotSecondaire));
  }, [lots]);

  // Lots secondaires non rattachés
  const lotsNonRattaches = useMemo(() => {
    return lotsSecondaires.filter(lot => !lot.lotPrincipalId);
  }, [lotsSecondaires]);

  // Lots rattachés à un lot principal donné
  const getLotsRattaches = (lotPrincipalId: string): Lot[] => {
    return sortLotsNumerically(
      lotsSecondaires.filter(lot => lot.lotPrincipalId === lotPrincipalId)
    );
  };

  // Tantièmes cumulés pour un lot principal
  const getTantiemesCumules = (lot: Lot): number => {
    if (!isLotPrincipal(lot)) return lot.tantièmesGeneraux;
    
    const rattaches = getLotsRattaches(lot.id);
    const totalRattaches = rattaches.reduce((sum, l) => sum + l.tantièmesGeneraux, 0);
    return lot.tantièmesGeneraux + totalRattaches;
  };

  // Session 47 : Trouver le nom du propriétaire d'un lot
  const getProprietaireNom = (lot: Lot): string => {
    // Priorité 1 : proprietaireId (nouveau système)
    if (lot.proprietaireId) {
      const copro = coproprietaires.find(c => c.id === lot.proprietaireId);
      if (copro) {
        return copro.prenom ? `${copro.prenom} ${copro.nom}` : copro.nom;
      }
    }
    // Priorité 2 : proprietaire embarqué (ancien système, rétrocompat)
    if (lot.proprietaire?.nom && lot.proprietaire.nom !== 'Non renseigné') {
      return lot.proprietaire.prenom 
        ? `${lot.proprietaire.prenom} ${lot.proprietaire.nom}`
        : lot.proprietaire.nom;
    }
    return 'Non renseigné';
  };

  // ============================================
  // CHARGEMENT
  // ============================================

  useEffect(() => {
    const loadData = async () => {
      if (!coproprieteId) return;
      
      try {
        setLoading(true);
        
        // Charger lots ET copropriétaires en parallèle
        const [lotsData, coprosData] = await Promise.all([
          getLotsByCopropriete(coproprieteId),
          getCoproprietaires(coproprieteId)
        ]);
        
        setLots(sortLotsNumerically(lotsData));
        setCoproprietaires(coprosData);
        
        const total = await calculerTotalTantiemes(coproprieteId);
        setTotalTantiemes(total);
      } catch (error) {
        console.error('Erreur chargement:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [coproprieteId]);

  // ============================================
  // HANDLERS CRUD
  // ============================================

  // Copier le prompt
  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(CSV_GENERATION_PROMPT);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (error) {
      console.error('Erreur copie:', error);
    }
  };

  // Ouvrir modal création
  const handleCreate = () => {
    setEditingLot(null);
    setFormData(emptyFormData);
    setShowModal(true);
  };

  // Ouvrir modal édition - Session 47 : Charger proprietaireId
  const handleEdit = (lot: Lot) => {
    setEditingLot(lot);
    setFormData({
      numero: lot.numero,
      type: lot.type,
      batiment: lot.batiment || '',
      etage: lot.etage || '',
      description: lot.description || '',
      tantièmesGeneraux: lot.tantièmesGeneraux,
      proprietaireId: lot.proprietaireId || '' // Session 47
    });
    setShowModal(true);
  };

  // Ouvrir modal détail (clic sur une ligne)
  const handleOpenDetail = (lot: Lot) => {
    setSelectedLot(lot);
    setShowDetailModal(true);
  };

  // Sauvegarder - Session 47 : Gérer proprietaireId
  const handleSave = async () => {
    if (!coproprieteId || !formData.numero) {
      alert('Veuillez remplir le numéro de lot');
      return;
    }
    
    setSaving(true);
    
    try {
      // Session 47 : Récupérer le copropriétaire sélectionné pour rétrocompat
      const selectedCopro = formData.proprietaireId 
        ? coproprietaires.find(c => c.id === formData.proprietaireId)
        : null;
      
      const lotData: LotCreateData = {
        coproprieteId,
        numero: formData.numero.trim(),
        type: formData.type,
        tantièmesGeneraux: formData.tantièmesGeneraux,
        // Session 47 : proprietaireId + rétrocompat proprietaire embarqué
        proprietaireId: formData.proprietaireId || undefined,
        proprietaire: {
          nom: selectedCopro ? selectedCopro.nom : 'Non renseigné',
          prenom: selectedCopro?.prenom
        }
      };
      
      // Champs optionnels
      if (formData.batiment.trim()) lotData.batiment = formData.batiment.trim();
      if (formData.etage.trim()) lotData.etage = formData.etage.trim();
      if (formData.description.trim()) lotData.description = formData.description.trim();
      
      if (editingLot) {
        await updateLot(editingLot.id, {
          numero: lotData.numero,
          type: lotData.type,
          batiment: lotData.batiment,
          etage: lotData.etage,
          description: lotData.description,
          tantièmesGeneraux: lotData.tantièmesGeneraux,
          proprietaireId: formData.proprietaireId || null, // null pour délier
          proprietaire: lotData.proprietaire
        });
      } else {
        await createLot(lotData);
      }
      
      // Recharger
      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      const total = await calculerTotalTantiemes(coproprieteId);
      setTotalTantiemes(total);
      
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde lot:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Confirmer suppression
  const handleDeleteClick = (lot: Lot, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setLotToDelete(lot);
    setShowDeleteConfirm(true);
  };

  // Supprimer
  const handleDeleteConfirm = async () => {
    if (!lotToDelete || !coproprieteId) return;
    
    setDeleting(true);
    
    try {
      // Si c'est un lot principal, détacher d'abord ses lots rattachés
      if (isLotPrincipal(lotToDelete)) {
        const rattaches = getLotsRattaches(lotToDelete.id);
        for (const lot of rattaches) {
          await updateLot(lot.id, { lotPrincipalId: null });
        }
      }
      
      await deleteLot(lotToDelete.id);
      
      // Recharger
      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      const total = await calculerTotalTantiemes(coproprieteId);
      setTotalTantiemes(total);
      
      setShowDeleteConfirm(false);
      setLotToDelete(null);
      setShowDetailModal(false);
      setSelectedLot(null);
    } catch (error) {
      console.error('Erreur suppression lot:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Supprimer tous les lots
  const handleDeleteAllConfirm = async () => {
    if (deleteConfirmText !== 'SUPPRIMER' || !coproprieteId) return;
    
    setDeletingAll(true);
    
    try {
      for (const lot of lots) {
        await deleteLot(lot.id);
      }
      
      setLots([]);
      setTotalTantiemes(0);
      setShowDeleteAllConfirm(false);
      setDeleteConfirmText('');
      
      alert(`${lots.length} lot(s) supprimé(s).`);
    } catch (error) {
      console.error('Erreur suppression en masse:', error);
      alert('Erreur lors de la suppression. Certains lots n\'ont peut-être pas été supprimés.');
      
      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      const total = await calculerTotalTantiemes(coproprieteId);
      setTotalTantiemes(total);
    } finally {
      setDeletingAll(false);
    }
  };

  // ============================================
  // HANDLERS RATTACHEMENT
  // ============================================

  // Rattacher un lot secondaire à un lot principal
  const handleRattacher = async (lotSecondaireId: string) => {
    if (!selectedLot || !coproprieteId) return;
    
    setRattachementLoading(true);
    
    try {
      await updateLot(lotSecondaireId, { lotPrincipalId: selectedLot.id });
      
      // Recharger
      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      
      // Mettre à jour le lot sélectionné
      const updatedLot = data.find(l => l.id === selectedLot.id);
      if (updatedLot) setSelectedLot(updatedLot);
      
      setShowRattachementModal(false);
    } catch (error) {
      console.error('Erreur rattachement:', error);
      alert('Erreur lors du rattachement');
    } finally {
      setRattachementLoading(false);
    }
  };

  // Détacher un lot secondaire
  const handleDetacher = async (lotSecondaireId: string) => {
    if (!coproprieteId) return;
    
    setRattachementLoading(true);
    
    try {
      await updateLot(lotSecondaireId, { lotPrincipalId: null });
      
      // Recharger
      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      
      // Mettre à jour le lot sélectionné
      if (selectedLot) {
        const updatedLot = data.find(l => l.id === selectedLot.id);
        if (updatedLot) setSelectedLot(updatedLot);
      }
    } catch (error) {
      console.error('Erreur détachement:', error);
      alert('Erreur lors du détachement');
    } finally {
      setRattachementLoading(false);
    }
  };

  // ============================================
  // EXTRACTION IA
  // ============================================

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !coproprieteId) return;

    e.target.value = '';

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non supporté. Utilisez un PDF ou une image (JPEG, PNG, WebP).');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Fichier trop volumineux. Taille maximum : 10 Mo.');
      return;
    }

    setExtracting(true);

    try {
      const result = await extractLotsFromDocument(file, coproprieteId);
      
      if (result.success && result.items.length > 0) {
        setExtractionResult(result);
        setShowExtractionModal(true);
      } else {
        const errorMsg = result.errors?.length > 0 
          ? result.errors.join(', ') 
          : 'Aucun lot n\'a pu être extrait de ce document.';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Erreur extraction:', error);
      alert('Erreur lors de l\'extraction. Veuillez réessayer.');
    } finally {
      setExtracting(false);
    }
  };

  const handleValidateExtraction = async (validatedLots: ExtractedLot[]) => {
    if (!coproprieteId || validatedLots.length === 0) return;

    setImportingLots(true);

    try {
      let importedCount = 0;

      for (const extractedLot of validatedLots) {
        const lotData: LotCreateData = {
          coproprieteId,
          numero: extractedLot.numero,
          type: mapExtractedTypeToTypeLot(extractedLot.type),
          tantièmesGeneraux: extractedLot.tantiemesGeneraux,
          proprietaire: {
            nom: 'Non renseigné'
          }
        };

        if (extractedLot.batiment) lotData.batiment = extractedLot.batiment;
        if (extractedLot.etage) lotData.etage = extractedLot.etage;
        if (extractedLot.description) lotData.description = extractedLot.description;
        if (extractedLot.numeroCommercial) {
          lotData.description = (lotData.description || '') + 
            (lotData.description ? ' — ' : '') + 
            `N° commercial: ${extractedLot.numeroCommercial}`;
        }

        await createLot(lotData);
        importedCount++;
      }

      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      const total = await calculerTotalTantiemes(coproprieteId);
      setTotalTantiemes(total);

      setShowExtractionModal(false);
      setExtractionResult(null);

      alert(`${importedCount} lot(s) importé(s) avec succès !`);
    } catch (error) {
      console.error('Erreur import lots:', error);
      alert('Erreur lors de l\'import des lots. Certains lots n\'ont peut-être pas été importés.');
    } finally {
      setImportingLots(false);
    }
  };

  const handleCloseExtractionModal = () => {
    setShowExtractionModal(false);
    setExtractionResult(null);
  };

  // ============================================
  // IMPORT CSV
  // ============================================

  const handleCSVImportClick = () => {
    csvInputRef.current?.click();
  };

  const parseCSV = (content: string): ExtractedLot[] => {
    const lines = content.trim().split('\n');
    if (lines.length < 2) return [];

    const separator = lines[0].includes(';') ? ';' : ',';
    const headers = lines[0].split(separator).map(h => h.trim().toLowerCase());
    
    const numIndex = headers.findIndex(h => h.includes('numéro') || h.includes('numero') || h === 'lot');
    const natureIndex = headers.findIndex(h => h.includes('nature'));
    const typeIndex = headers.findIndex(h => h === 'type');
    const etageIndex = headers.findIndex(h => h.includes('étage') || h.includes('etage'));
    const tantiemesIndex = headers.findIndex(h => h.includes('tantième') || h.includes('tantieme'));

    if (numIndex === -1 || tantiemesIndex === -1) {
      throw new Error('Colonnes obligatoires manquantes (Numéro de lot, Tantièmes)');
    }

    const lotsExtraits: ExtractedLot[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(separator).map(v => v.trim());
      
      const numero = values[numIndex] || '';
      const nature = natureIndex >= 0 ? values[natureIndex] || '' : '';
      const type = typeIndex >= 0 ? values[typeIndex] || '' : '';
      const etage = etageIndex >= 0 ? values[etageIndex] || '' : '';
      const tantiemes = parseInt(values[tantiemesIndex]) || 0;

      if (!numero) continue;

      // Construire la description (type d'appartement T1, T2, etc.)
      let description = '';
      if (type && nature.toLowerCase().includes('appartement')) {
        description = type; // T1, T2, T3, etc.
      }

      lotsExtraits.push({
        tempId: `csv_${Date.now()}_${i}`,
        numero,
        type: nature || 'Autre', // Garder la nature originale pour l'affichage
        etage: etage || '',
        tantiemesGeneraux: tantiemes,
        description: description || undefined,
        selected: true,
        hasError: false
      });
    }

    return lotsExtraits;
  };

  const handleCSVSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !coproprieteId) return;

    e.target.value = '';

    if (!file.name.endsWith('.csv')) {
      alert('Veuillez sélectionner un fichier CSV.');
      return;
    }

    setImportingCSV(true);

    try {
      const content = await file.text();
      const parsedLots = parseCSV(content);

      if (parsedLots.length === 0) {
        alert('Aucun lot trouvé dans le fichier CSV.');
        return;
      }

      const result: LotsExtractionResult = {
        success: true,
        type: 'lots',
        documentName: file.name,
        extractedAt: new Date(),
        items: parsedLots,
        totalFound: parsedLots.length,
        confidence: 'high',
        warnings: [],
        errors: []
      };

      setExtractionResult(result);
      setShowExtractionModal(true);

    } catch (error) {
      console.error('Erreur parsing CSV:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de la lecture du fichier CSV.');
    } finally {
      setImportingCSV(false);
    }
  };

  // Surcharge de handleValidateExtraction pour utiliser le bon mapping CSV
  const handleValidateCSV = async (validatedLots: ExtractedLot[]) => {
    if (!coproprieteId || validatedLots.length === 0) return;

    setImportingLots(true);

    try {
      let importedCount = 0;

      for (const extractedLot of validatedLots) {
        const lotData: LotCreateData = {
          coproprieteId,
          numero: extractedLot.numero,
          type: mapNatureToTypeLot(extractedLot.type), // Mapping intelligent
          tantièmesGeneraux: extractedLot.tantiemesGeneraux,
          proprietaire: {
            nom: 'Non renseigné'
          }
        };

        if (extractedLot.etage) lotData.etage = extractedLot.etage;
        if (extractedLot.description) lotData.description = extractedLot.description;

        await createLot(lotData);
        importedCount++;
      }

      const data = await getLotsByCopropriete(coproprieteId);
      setLots(sortLotsNumerically(data));
      const total = await calculerTotalTantiemes(coproprieteId);
      setTotalTantiemes(total);

      setShowExtractionModal(false);
      setExtractionResult(null);

      alert(`${importedCount} lot(s) importé(s) avec succès !`);
    } catch (error) {
      console.error('Erreur import lots:', error);
      alert('Erreur lors de l\'import des lots.');
    } finally {
      setImportingLots(false);
    }
  };

  // ============================================
  // RENDU
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lots & Copropriétaires</h1>
          <p className="text-gray-600">
            {lotsPrincipaux.length} appartement{lotsPrincipaux.length > 1 ? 's' : ''} — {totalTantiemes.toLocaleString()} tantièmes
            {lotsSecondaires.length > 0 && (
              <span className="text-gray-400 ml-2">
                ({lotsSecondaires.length} lot{lotsSecondaires.length > 1 ? 's' : ''} secondaire{lotsSecondaires.length > 1 ? 's' : ''})
              </span>
            )}
          </p>
        </div>
        
        {lots.length > 0 && (
          <div className="flex gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,image/*"
              className="hidden"
            />
            <input
              type="file"
              ref={csvInputRef}
              onChange={handleCSVSelect}
              accept=".csv"
              className="hidden"
            />
            
            {/* Session 75 : Bouton "Tout supprimer" visible uniquement pour Admin SaaS */}
            {canDeleteAll && (
              <button
                onClick={() => setShowDeleteAllConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Tout supprimer
              </button>
            )}
            
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter un lot
            </button>
          </div>
        )}
      </div>

      {/* État vide avec instructions */}
      {lots.length === 0 && (
        <div className="bg-white rounded-lg border p-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Home className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucun lot enregistré</h2>
              <p className="text-gray-600">Importez les lots de votre copropriété depuis un fichier CSV.</p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-4">Comment générer le fichier CSV ?</h3>
              
              <ol className="space-y-3 text-sm text-blue-800">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Récupérez votre règlement de copropriété (fichier PDF)</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Ouvrez <strong>Claude.ai</strong> (gratuit sur <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="underline">claude.ai</a>)</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Uploadez le PDF et collez le prompt ci-dessous</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Copiez le résultat dans un fichier texte, enregistrez-le en <strong>.csv</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <span>Importez le fichier CSV ici</span>
                </li>
              </ol>
            </div>

            {/* Prompt à copier */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Prompt à copier :</label>
                <button
                  onClick={handleCopyPrompt}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  {promptCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copier
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                {CSV_GENERATION_PROMPT}
              </pre>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-center gap-4">
              <input
                type="file"
                ref={csvInputRef}
                onChange={handleCSVSelect}
                accept=".csv"
                className="hidden"
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,image/*"
                className="hidden"
              />
              
              <button
                onClick={handleCSVImportClick}
                disabled={importingCSV}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {importingCSV ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-5 h-5" />
                )}
                Importer un fichier CSV
              </button>
              
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Ajouter manuellement
              </button>
            </div>

            {/* Lien alternatif extraction IA */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Vous avez un petit document (moins de 20 pages) ?{' '}
              <button
                onClick={handleImportClick}
                disabled={extracting}
                className="text-blue-600 hover:underline"
              >
                Essayez l'extraction automatique
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Alerte lots non rattachés (EN HAUT pour visibilité) */}
      {lots.length > 0 && lotsNonRattaches.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg">
          <button
            onClick={() => setShowLotsNonRattaches(!showLotsNonRattaches)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <div className="flex items-center gap-3">
              <Unlink className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-900">
                {lotsNonRattaches.length} lot{lotsNonRattaches.length > 1 ? 's' : ''} non rattaché{lotsNonRattaches.length > 1 ? 's' : ''}
              </span>
              <span className="text-amber-700 text-sm">
                (caves, parkings, locaux vélos sans appartement associé)
              </span>
            </div>
            {showLotsNonRattaches ? (
              <ChevronUp className="w-5 h-5 text-amber-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-amber-600" />
            )}
          </button>
          
          {showLotsNonRattaches && (
            <div className="border-t border-amber-200 p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {lotsNonRattaches.map((lot) => (
                  <div 
                    key={lot.id}
                    className="flex items-center gap-2 p-3 bg-white rounded-lg border border-amber-200"
                  >
                    <span className="text-amber-600">
                      {getLotIcon(lot.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        Lot {lot.numero}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {TYPE_LOT_LABELS[lot.type]} — {lot.tantièmesGeneraux} ‰
                      </p>
                    </div>
                    <button
                      onClick={() => handleEdit(lot)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-sm text-amber-700 mt-4">
                Pour rattacher un lot, cliquez sur un appartement dans le tableau ci-dessous, puis utilisez le bouton "Rattacher un lot".
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tableau des lots principaux (appartements) */}
      {lots.length > 0 && (
        <>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriétaire
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lots rattachés
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tantièmes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lotsPrincipaux.map((lot) => {
                  const rattaches = getLotsRattaches(lot.id);
                  const tantiemesCumules = getTantiemesCumules(lot);
                  
                  return (
                    <tr 
                      key={lot.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOpenDetail(lot)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">
                            {getLotIcon(lot.type)}
                          </span>
                          <div>
                            <span className="font-medium text-gray-900">{lot.numero}</span>
                            {lot.etage && (
                              <span className="text-gray-500 text-sm ml-2">({lot.etage})</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-gray-600">
                          {TYPE_LOT_LABELS[lot.type] || lot.type}
                        </span>
                        {lot.description && (
                          <span className="text-gray-400 text-sm ml-1">
                            {lot.description}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {getProprietaireNom(lot)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {rattaches.length > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            <Link className="w-3 h-3" />
                            {rattaches.length} lot{rattaches.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 font-medium">
                          {tantiemesCumules.toLocaleString()}
                        </span>
                        {rattaches.length > 0 && (
                          <span className="text-gray-400 text-xs ml-1">
                            ({lot.tantièmesGeneraux.toLocaleString()} + {(tantiemesCumules - lot.tantièmesGeneraux).toLocaleString()})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(lot);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {/* Session 75 : Corbeille visible uniquement pour Gestionnaire/Admin SaaS */}
                          {canDeleteLot && (
                            <button
                              onClick={(e) => handleDeleteClick(lot, e)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Modal création/édition - Session 47 : Sélecteur copropriétaire */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingLot ? 'Modifier le lot' : 'Ajouter un lot'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Numéro et Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro du lot *
                  </label>
                  <input
                    type="text"
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1, 2, 3..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as TypeLot })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(TYPE_LOT_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Bâtiment et Étage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bâtiment
                  </label>
                  <input
                    type="text"
                    value={formData.batiment}
                    onChange={(e) => setFormData({ ...formData, batiment: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="A, B, Principal..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Étage
                  </label>
                  <input
                    type="text"
                    value={formData.etage}
                    onChange={(e) => setFormData({ ...formData, etage: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="RDC, 1, 2, -1..."
                  />
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="T2, T3, T4..."
                />
              </div>
              
              {/* Tantièmes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tantièmes généraux *
                </label>
                <input
                  type="number"
                  value={formData.tantièmesGeneraux}
                  onChange={(e) => setFormData({ ...formData, tantièmesGeneraux: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              
              {/* Session 47 : Sélecteur Propriétaire */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Propriétaire
                  </span>
                </label>
                <select
                  value={formData.proprietaireId}
                  onChange={(e) => setFormData({ ...formData, proprietaireId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">— Non renseigné —</option>
                  {coproprietaires
                    .sort((a, b) => a.nom.localeCompare(b.nom))
                    .map((copro) => (
                      <option key={copro.id} value={copro.id}>
                        {copro.nom}{copro.prenom ? ` ${copro.prenom}` : ''}
                      </option>
                    ))
                  }
                </select>
                {coproprietaires.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Aucun copropriétaire enregistré. Ajoutez d'abord des copropriétaires dans le menu "Copropriétaires".
                  </p>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal détail lot principal */}
      {showDetailModal && selectedLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <div className="flex items-center gap-3">
                <span className="text-blue-600">
                  {getLotIcon(selectedLot.type)}
                </span>
                <div>
                  <h2 className="text-lg font-semibold">
                    Lot {selectedLot.numero}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {TYPE_LOT_LABELS[selectedLot.type]}
                    {selectedLot.description && ` — ${selectedLot.description}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedLot(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Infos générales */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Étage :</span>
                  <span className="ml-2 text-gray-900">{selectedLot.etage || 'Non précisé'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Tantièmes propres :</span>
                  <span className="ml-2 text-gray-900 font-medium">{selectedLot.tantièmesGeneraux.toLocaleString()}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Propriétaire :</span>
                  <span className="ml-2 text-gray-900">
                    {getProprietaireNom(selectedLot)}
                  </span>
                </div>
              </div>
              
              {/* Lots rattachés */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  Lots rattachés
                </h3>
                
                {getLotsRattaches(selectedLot.id).length > 0 ? (
                  <div className="space-y-2">
                    {getLotsRattaches(selectedLot.id).map((lot) => (
                      <div 
                        key={lot.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">
                            {getLotIcon(lot.type)}
                          </span>
                          <div>
                            <span className="font-medium text-gray-900">Lot {lot.numero}</span>
                            <p className="text-sm text-gray-500">
                              {TYPE_LOT_LABELS[lot.type]} — {lot.tantièmesGeneraux} ‰
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDetacher(lot.id)}
                          disabled={rattachementLoading}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Détacher ce lot"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {/* Total tantièmes */}
                    <div className="flex justify-between p-3 bg-blue-50 rounded-lg font-medium">
                      <span className="text-blue-900">Total tantièmes</span>
                      <span className="text-blue-900">{getTantiemesCumules(selectedLot).toLocaleString()} ‰</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun lot rattaché</p>
                )}
                
                {lotsNonRattaches.length > 0 && (
                  <button
                    onClick={() => setShowRattachementModal(true)}
                    className="mt-4 flex items-center gap-2 px-4 py-2 w-full justify-center text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <Link className="w-4 h-4" />
                    Rattacher un lot
                  </button>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-between gap-3 p-4 border-t bg-gray-50">
              {/* Session 75 : Bouton Supprimer visible uniquement pour Gestionnaire/Admin SaaS */}
              {canDeleteLot ? (
                <button
                  onClick={() => handleDeleteClick(selectedLot)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Supprimer
                </button>
              ) : (
                <div></div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEdit(selectedLot);
                  }}
                  className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedLot(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal sélection lot à rattacher */}
      {showRattachementModal && selectedLot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Rattacher un lot au lot {selectedLot.numero}
              </h2>
              <button
                onClick={() => setShowRattachementModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Sélectionnez le lot à rattacher à cet appartement :
              </p>
              
              <div className="max-h-80 overflow-y-auto space-y-2">
                {lotsNonRattaches.map((lot) => (
                  <button
                    key={lot.id}
                    onClick={() => handleRattacher(lot.id)}
                    disabled={rattachementLoading}
                    className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400">
                        {getLotIcon(lot.type)}
                      </span>
                      <div className="text-left">
                        <span className="font-medium text-gray-900">Lot {lot.numero}</span>
                        <p className="text-sm text-gray-500">
                          {TYPE_LOT_LABELS[lot.type]} — {lot.tantièmesGeneraux} ‰
                        </p>
                      </div>
                    </div>
                    <Link className="w-4 h-4 text-blue-600" />
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowRattachementModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {showDeleteConfirm && lotToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmer la suppression
            </h2>
            <p className="text-gray-600 mb-2">
              Êtes-vous sûr de vouloir supprimer le lot <strong>{lotToDelete.numero}</strong> ?
            </p>
            {isLotPrincipal(lotToDelete) && getLotsRattaches(lotToDelete.id).length > 0 && (
              <p className="text-amber-600 text-sm mb-4">
                Les {getLotsRattaches(lotToDelete.id).length} lot(s) rattaché(s) seront automatiquement détachés.
              </p>
            )}
            <p className="text-gray-500 text-sm mb-6">
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setLotToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression en masse */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-red-600 mb-4">
              Supprimer tous les lots
            </h2>
            <p className="text-gray-600 mb-4">
              Vous êtes sur le point de supprimer <strong>{lots.length} lot(s)</strong>.
              Cette action est irréversible.
            </p>
            <p className="text-gray-600 mb-4">
              Pour confirmer, tapez <strong>SUPPRIMER</strong> ci-dessous :
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-6"
              placeholder="Tapez SUPPRIMER"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteAllConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAllConfirm}
                disabled={deleteConfirmText !== 'SUPPRIMER' || deletingAll}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingAll ? 'Suppression...' : 'Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal validation extraction */}
      {showExtractionModal && extractionResult && (
        <ExtractionValidationModal
          isOpen={showExtractionModal}
          onClose={handleCloseExtractionModal}
          lotsResult={extractionResult}
          onConfirmLots={extractionResult.documentName?.endsWith('.csv') ? handleValidateCSV : handleValidateExtraction}
          isImporting={importingLots}
        />
      )}
    </div>
  );
};

export default LotsPage;
